import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { PageHeader } from '../components/shared/PageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { StatusBadge } from '../components/shared/StatusBadge';
import { UrgencyBadge } from '../components/shared/UrgencyBadge';
import { useAppContext } from '../lib/store';
import { Edit, Mail, Phone, MapPin, Building, ArrowRight } from 'lucide-react';
export function CustomerDetailPage() {
  const {
    id
  } = useParams();
  const navigate = useNavigate();
  const {
    customers,
    workOrders
  } = useAppContext();
  const customer = customers.find(c => c.id === id);
  if (!customer) {
    return <div data-id="element-173">Stranka ne obstaja.</div>;
  }
  const customerWorkOrders = workOrders.filter(wo => wo.customer_id === id).sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
  return <div className="space-y-6" data-id="element-174">
      <PageHeader title={customer.name} description="Podrobnosti stranke in pregled delovnih nalogov." data-id="element-175">
        <Button onClick={() => navigate(`/stranke/${id}/uredi`)} data-id="element-176">
          <Edit className="w-4 h-4 mr-2" data-id="element-177" />
          Uredi
        </Button>
      </PageHeader>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-178">
        {/* Customer Info */}
        <Card className="md:col-span-1" data-id="element-179">
          <CardHeader data-id="element-180">
            <CardTitle className="text-lg flex items-center justify-between" data-id="element-181">
              Osnovni podatki
              {customer.is_active ? <Badge variant="default" className="bg-green-100 text-green-700" data-id="element-182">
                  Aktivna
                </Badge> : <Badge variant="secondary" data-id="element-183">Neaktivna</Badge>}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6" data-id="element-184">
            <div data-id="element-185">
              <p className="text-sm text-slate-500 mb-1" data-id="element-186">Predpona nalogov</p>
              <Badge variant="outline" className="font-mono text-base" data-id="element-187">
                {customer.prefix}
              </Badge>
            </div>

            <div className="space-y-3" data-id="element-188">
              <div className="flex items-start gap-3" data-id="element-189">
                <Building className="w-5 h-5 text-slate-400 mt-0.5" data-id="element-190" />
                <div data-id="element-191">
                  <p className="text-sm font-medium" data-id="element-192">Davčna številka</p>
                  <p className="text-sm text-slate-600" data-id="element-193">
                    {customer.tax_number || '/'}
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3" data-id="element-194">
                <MapPin className="w-5 h-5 text-slate-400 mt-0.5" data-id="element-195" />
                <div data-id="element-196">
                  <p className="text-sm font-medium" data-id="element-197">Naslov</p>
                  <p className="text-sm text-slate-600" data-id="element-198">
                    {customer.address}
                    <br data-id="element-199" />
                    {customer.postal_code} {customer.city}
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3" data-id="element-200">
                <Mail className="w-5 h-5 text-slate-400 mt-0.5" data-id="element-201" />
                <div data-id="element-202">
                  <p className="text-sm font-medium" data-id="element-203">E-pošta</p>
                  <p className="text-sm text-slate-600" data-id="element-204">
                    {customer.email || '/'}
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3" data-id="element-205">
                <Phone className="w-5 h-5 text-slate-400 mt-0.5" data-id="element-206" />
                <div data-id="element-207">
                  <p className="text-sm font-medium" data-id="element-208">Telefon</p>
                  <p className="text-sm text-slate-600" data-id="element-209">
                    {customer.phone || '/'}
                  </p>
                </div>
              </div>
            </div>

            {customer.notes && <div className="pt-4 border-t" data-id="element-210">
                <p className="text-sm font-medium mb-1" data-id="element-211">Opombe</p>
                <p className="text-sm text-slate-600 whitespace-pre-wrap" data-id="element-212">
                  {customer.notes}
                </p>
              </div>}
          </CardContent>
        </Card>

        {/* Work Orders List */}
        <Card className="md:col-span-2" data-id="element-213">
          <CardHeader className="flex flex-row items-center justify-between" data-id="element-214">
            <CardTitle className="text-lg" data-id="element-215">
              Delovni nalogi ({customerWorkOrders.length})
            </CardTitle>
            <Button variant="outline" size="sm" onClick={() => navigate('/nalogi/nov')} data-id="element-216">
              Nov nalog za stranko
            </Button>
          </CardHeader>
          <CardContent data-id="element-217">
            {customerWorkOrders.length > 0 ? <div className="rounded-md border" data-id="element-218">
                <Table data-id="element-219">
                  <TableHeader data-id="element-220">
                    <TableRow data-id="element-221">
                      <TableHead data-id="element-222">Številka</TableHead>
                      <TableHead data-id="element-223">Datum</TableHead>
                      <TableHead data-id="element-224">Nujnost</TableHead>
                      <TableHead data-id="element-225">Status</TableHead>
                      <TableHead data-id="element-226"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody data-id="element-227">
                    {customerWorkOrders.map(wo => <TableRow key={wo.id} data-id="element-228">
                        <TableCell className="font-medium text-primary" data-id="element-229">
                          {wo.work_order_number}
                        </TableCell>
                        <TableCell data-id="element-230">
                          {new Date(wo.execution_date).toLocaleDateString('sl-SI')}
                        </TableCell>
                        <TableCell data-id="element-231">
                          <UrgencyBadge urgency={wo.urgency} data-id="element-232" />
                        </TableCell>
                        <TableCell data-id="element-233">
                          <StatusBadge status={wo.status} data-id="element-234" />
                        </TableCell>
                        <TableCell className="text-right" data-id="element-235">
                          <Button variant="ghost" size="sm" onClick={() => navigate(`/nalogi/${wo.id}`)} data-id="element-236">
                            Odpri <ArrowRight className="w-4 h-4 ml-1" data-id="element-237" />
                          </Button>
                        </TableCell>
                      </TableRow>)}
                  </TableBody>
                </Table>
              </div> : <div className="text-center py-12 text-slate-500 border rounded-lg border-dashed" data-id="element-238">
                Za to stranko še ni ustvarjenih delovnih nalogov.
              </div>}
          </CardContent>
        </Card>
      </div>
    </div>;
}